import React from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";

import { chf } from "./financeFormat";
import { selectCostRankingRows } from "./financeSelectors";

/**
 * "Kostenintensivste Einheiten" — the top units by operating cost this month.
 *
 * THE SOURCE IS `/dashboard/cost-ranking`, NOT `revenue-analytics.top_units`.
 * Both exist, both are called "top units", and they rank different things:
 * cost-ranking orders on `operating_cost_total_chf`
 * (`routes_admin_dashboard_costs.py:102`), while `top_units` orders on
 * `expected_recurring_rent` (`expected_revenue_analytics_service.py:324`). A
 * card titled "kostenintensivste" fed by the revenue list would be a plausible
 * list of the wrong units — the failure mode nobody notices, because it looks
 * exactly like a correct answer. `companyFinanceContract.test.js` keeps them
 * apart.
 *
 * THE ORDER AND THE MEMBERSHIP ARE THE CONTRACT'S. No client sort and no client
 * filter. A unit whose lease overlaps the month is ranked even at CHF 0 — that
 * is a real result about a real unit — and a unit whose lease has not begun is
 * absent from the response entirely, which is not the same as "cheap". Dropping
 * the zero rows here would delete an answer; re-sorting would risk ordering on
 * `unclassified_total_chf`, which the contract deliberately keeps out of the
 * ranking.
 *
 * FIVE IS THE REQUEST, NOT THE PORTFOLIO. `limit=5` is sent and whatever comes
 * back is rendered — three units render three rows. There are no placeholder
 * rows: an empty row would state that a fifth unit exists.
 *
 * NO DELTAS, NO SHARES. The contract carries no comparison for this ranking, and
 * a percentage of what would need a denominator this card has no claim to.
 *
 * THE IDENTITY IS THE SHORT CODE, THE ADDRESS IS THE PROOF (F2c.1). The rows
 * used to lead with `title` over `city`, which reads "Zürich / Zürich" for every
 * unit of an organisation that names its units after their location — a ranking
 * that names no unit at all. They now lead with `short_unit_id` over the full
 * postal address, through the same `getUnitHeadingLabel` /
 * `getUnitAddressSubtitle` contract twenty other surfaces already read. The
 * selector holds that logic; this file only places the two lines.
 */

const CARD = "rounded-xl border border-border bg-card p-4 shadow-sm";

function CardShell({ children, subtitle }) {
  return (
    <section
      aria-labelledby="finance-cost-ranking-heading"
      className={CARD}
      data-testid="finance-cost-ranking-card"
    >
      <h2
        id="finance-cost-ranking-heading"
        className="text-[13px] font-semibold text-card-foreground"
      >
        Kostenintensivste Einheiten
      </h2>
      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">
        {subtitle}
      </p>
      {children}
    </section>
  );
}

export default function FinanceCostRankingCard({
  costRanking,
  window: periodWindow,
  loading = false,
  onRetry,
}) {
  const error = costRanking?.error;
  const isRange = Boolean(periodWindow?.isRange);
  const subtitle = isRange
    ? "Nach Betriebskosten im gewählten Zeitraum"
    : "Nach Betriebskosten im ausgewählten Monat";
  const empty = isRange
    ? "Keine Einheiten mit Betriebskosten in diesem Zeitraum."
    : "Keine Einheiten mit Betriebskosten in diesem Monat.";

  if (loading) {
    return (
      <CardShell subtitle={subtitle}>
        <div className="mt-3 space-y-2" aria-busy="true">
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-full" />
          <Skeleton className="h-8 w-2/3" />
        </div>
      </CardShell>
    );
  }

  if (error) {
    return (
      <CardShell subtitle={subtitle}>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <p role="alert" className="text-[12px] text-destructive">
            Kostenranking konnte nicht geladen werden.
          </p>
          {onRetry ? (
            <button
              type="button"
              onClick={onRetry}
              className="rounded-md border border-border px-2 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
            >
              Erneut laden
            </button>
          ) : null}
        </div>
      </CardShell>
    );
  }

  const rows = selectCostRankingRows(costRanking?.data);

  if (rows.length === 0) {
    return (
      <CardShell subtitle={subtitle}>
        <p className="mt-4 text-[12px] text-muted-foreground">
          {empty}
        </p>
      </CardShell>
    );
  }

  return (
    <CardShell subtitle={subtitle}>
      <ul className="mt-3 list-none divide-y divide-border p-0" data-testid="finance-cost-ranking-rows">
        {rows.map((row) => {
          const amount = chf(row.raw);
          const content = (
            <>
              <span className="flex min-w-0 items-baseline gap-2">
                <span className="shrink-0 text-[11px] font-medium tabular-nums text-muted-foreground">
                  {row.rank}.
                </span>
                <span className="min-w-0">
                  {/* `break-words`, not `truncate`: a long unit title on a 375px
                      column is still the row's identity, and cutting it off is
                      how two units become indistinguishable. */}
                  <span className="block break-words text-[12px] font-medium text-card-foreground">
                    {row.label ?? "—"}
                  </span>
                  {row.location ? (
                    <span className="block break-words text-[10px] text-muted-foreground">
                      {row.location}
                    </span>
                  ) : null}
                </span>
              </span>
              {/* The amount is text, never a bar width alone. */}
              <span className="shrink-0 text-[12px] font-semibold tabular-nums text-card-foreground">
                {amount ?? "—"}
              </span>
            </>
          );

          return (
            <li key={row.unitId ?? row.rank} className="py-2 first:pt-0 last:pb-0">
              {row.unitId ? (
                <Link
                  to={`/admin/units/${encodeURIComponent(row.unitId)}`}
                  data-testid={`finance-cost-ranking-unit-${row.unitId}`}
                  className="flex items-start justify-between gap-3 rounded-[6px] px-1 py-0.5 transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  {content}
                </Link>
              ) : (
                <div className="flex items-start justify-between gap-3 px-1 py-0.5">
                  {content}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </CardShell>
  );
}
